/**
 * useSectionMapping — Derives mapped sections from editor state.
 * Phase 3B: reads tree data from TanStack Query cache (via useScenarioTree state).
 * The mounted boolean toggle is eliminated — query data changes trigger re-renders.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { mapTreeToSections } from '../../../utils/modelDataMapper';
import type { EditorState } from '../../../shared/hooks/queries/useScenarioTree';

interface UseSectionMappingParams {
    state: EditorState;
    computeErrors: any[];
    currentSensitivity: any;
}

export function useSectionMapping({
    state,
    computeErrors,
    currentSensitivity,
}: UseSectionMappingParams) {
    const [sectionsLoading, setSectionsLoading] = useState(false);

    const mappedSections = useMemo(() => {
        if (!state || !state['Input'] || !state['Output']) {
            return [];
        }

        try {
            const inputRootId = Object.values(state['Input'].tree.items)
                .find((item: any) => !item.parentId || item.parentId === 'null')?.id || '42';

            const outputRootId = Object.values(state['Output'].tree.items)
                .find((item: any) => !item.parentId || item.parentId === 'null')?.id || '42';

            const inputItems = Object.values(state['Input'].tree.items)
                .filter((item: any) => item.id !== inputRootId)
                .map((item: any) => ({
                    id: parseInt(item.id),
                    name: item.name,
                    type: item.type,
                    kind: item.kind,
                    parent_id: item.parentId ? parseInt(item.parentId) : null,
                    position: item.position,
                    childrens: item.childrens.map((c: string) => parseInt(c)),
                    fields: item.fields,
                    possible_childrens: item.possibleChildrens,
                    childrens_definition: item.childrensDefinition,
                    related_model_id: item.relatedModelDataId,
                    has_related_output: item.has_related_output,
                    is_inconsiderable: item.isInconsiderable,
                    is_deletable: item.isDeletable,
                    documentation: item.documentation,
                }));

            const outputItems = Object.values(state['Output'].tree.items)
                .filter((item: any) => item.id !== outputRootId)
                .map((item: any) => ({
                    id: parseInt(item.id),
                    name: item.name,
                    type: item.type,
                    kind: item.kind,
                    parent_id: item.parentId ? parseInt(item.parentId) : null,
                    position: item.position,
                    childrens: item.childrens.map((c: string) => parseInt(c)),
                    fields: item.fields,
                    possible_childrens: item.possibleChildrens,
                    childrens_definition: item.childrensDefinition,
                    related_model_id: item.relatedModelDataId,
                    has_related_output: item.has_related_output,
                    is_inconsiderable: item.isInconsiderable,
                    is_deletable: item.isDeletable,
                    documentation: item.documentation,
                }));

            return mapTreeToSections(inputItems, outputItems);
        } catch (error) {
            console.error('Error remapping sections:', error);
            return [];
        }
    }, [state]);

    useEffect(() => {
        setSectionsLoading(true);
        setSectionsLoading(false);
    }, [mappedSections]);

    const getAllComputeErrors = useCallback(() => {
        const seen = new Set<string>();
        const result: Array<{ itemId: string; itemName: string; fieldName: string; errorMessage: string; type: 'compute'; kind?: string; source?: string; _path?: any }> = [];

        // 1. Errors from the API (computeErrors) — scenario errors only (sensitivity errors shown separately)
        for (const err of computeErrors) {
            if (err.source === 'sensitivity') continue;
            const key = `${err.model_data_id}-${err.field_name}-${err.kind}`;
            if (seen.has(key)) continue;
            seen.add(key);
            result.push({
                itemId: err.model_data_id.toString(),
                itemName: err.model_data_name,
                fieldName: err.field_display_name || '',
                errorMessage: err.error,
                type: 'compute' as const,
                kind: err.kind,
                source: err.source,
                _path: err.path,
            });
        }

        // 2. Errors from the tree state (updated in real-time via WebSocket)
        //    This ensures errors appear immediately after compute, without
        //    waiting for a full API reload.
        if (state?.['Input']?.tree?.items) {
            for (const item of Object.values(state['Input'].tree.items) as any[]) {
                if (!item.fields) continue;
                for (const [fieldName, field] of Object.entries(item.fields) as any[]) {
                    if (field.compute_error) {
                        const key = `${item.id}-${fieldName}-${item.kind}`;
                        if (seen.has(key)) continue;
                        seen.add(key);
                        result.push({
                            itemId: item.id.toString(),
                            itemName: item.name,
                            fieldName: field.display_name || fieldName,
                            errorMessage: field.compute_error,
                            type: 'compute' as const,
                        });
                    }
                }
            }
        }

        return result;
    }, [computeErrors, state]);

    const isDashboardComputed = useCallback(() => {
        if (currentSensitivity == 'null') {
            return state.dashboardComputed;
        } else {
            return currentSensitivity && currentSensitivity.dashboard_computed;
        }
    }, [state, currentSensitivity]);

    return {
        mappedSections,
        sectionsLoading,
        getAllComputeErrors,
        isDashboardComputed,
    };
}
